/**
 * React Query hooks + helpers for the Telemetry viewer (/it/telemetry and its
 * per-user drill-down at /it/telemetry/user).
 *
 *   useTelemetryReport(report, hours)        GET {API_BASE}/v1/cockpit-ops/telemetry
 *   useTelemetryDetail(report, params, on)   GET {API_BASE}/v1/cockpit-ops/telemetry/detail
 *   downloadTelemetryXlsx(report, hours, p)  GET {API_BASE}/v1/cockpit-ops/telemetry/export
 *
 * Each call runs one canned CloudWatch Logs Insights query server-side and
 * returns its rows. Insights is asynchronous and billed per GB scanned, so a
 * request can take several seconds and the time window is always explicit.
 * Nothing is cached aggressively (staleTime 60s) and nothing auto-refetches: a
 * background poll would quietly spend money.
 *
 * All timestamps in the response (ts / last_seen / fetched_at) are ISO 8601
 * strings, so every date/time value renders through `formatDateTime` from
 * `dateUtils.ts`. Sort and search are client-side over the rows already
 * loaded for the selected report and time range; the pure helpers below
 * (`sortTelemetryRows`, `matchesSearch`) are exported so they can be unit
 * tested without a network call.
 *
 * RBAC: same as cockpit-ops (cockpit-ops:read), server-enforced.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SortDir } from '@ampersandbrands/ui';
import { apiGet } from './api';
import { API_BASE } from './apiBase';
import { formatDateTime } from './dateUtils';

export const TELEMETRY_API_BASE = `${API_BASE}/v1/cockpit-ops`;

export type TelemetryReportId =
  | 'page_visits'
  | 'top_pages'
  | 'errors_by_site'
  | 'slow_routes'
  | 'activity_by_user'
  | 'api_errors';

/** The two canned drill-down queries behind GET .../telemetry/detail. */
export type TelemetryDetailReportId = 'api_error_detail' | 'user_activity_detail';

export type TelemetryRow = Record<string, string>;

export type TelemetryReportResponse = {
  report: string;
  label: string;
  source: 'access' | 'telemetry';
  log_group: string;
  hours: number;
  /** False when the log group does not exist yet (the browser collector has not shipped). */
  available: boolean;
  columns: string[];
  rows: TelemetryRow[];
  bytes_scanned: number;
  fetched_at: string;
  note?: string;
};

export type UseTelemetryReportResult = {
  data: TelemetryReportResponse | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<TelemetryReportResponse, Error>;
};

/** The Report / Time-range picks the whole page shares. Kept here (not local
 * to Telemetry.tsx) so the per-user drill-down page offers the same options. */
export const RANGES: Array<{ hours: number; label: string }> = [
  { hours: 1, label: 'Last hour' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 72, label: 'Last 3 days' },
  { hours: 168, label: 'Last 7 days' },
];

export const DEFAULT_HOURS = 24;

export function useTelemetryReport(
  report: TelemetryReportId,
  hours: number,
): UseTelemetryReportResult {
  const query = useQuery<TelemetryReportResponse, Error>({
    queryKey: ['telemetry-report', report, hours],
    queryFn: () =>
      apiGet<TelemetryReportResponse>(
        `${TELEMETRY_API_BASE}/telemetry?report=${encodeURIComponent(report)}&hours=${hours}`,
      ),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

// ---------------------------------------------------------------------------
// Drill-down detail query
// ---------------------------------------------------------------------------

export type TelemetryDetailParams = {
  hours: number;
  status?: number | string;
  route?: string;
  email?: string;
};

export type UseTelemetryDetailResult = {
  data: TelemetryReportResponse | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
};

function buildTelemetryQuery(
  report: TelemetryReportId | TelemetryDetailReportId,
  params: TelemetryDetailParams,
): URLSearchParams {
  const qs = new URLSearchParams({ report, hours: String(params.hours) });
  if (params.status !== undefined && params.status !== '') qs.set('status', String(params.status));
  if (params.route) qs.set('route', params.route);
  if (params.email) qs.set('email', params.email);
  return qs;
}

/**
 * GET .../telemetry/detail?report=api_error_detail&status=&route=&hours=
 * or   .../telemetry/detail?report=user_activity_detail&email=&hours=
 *
 * `enabled` gates the fetch (the api-error modal only mounts on click; the
 * user drill-down page waits for a non-empty email in the URL).
 */
export function useTelemetryDetail(
  report: TelemetryDetailReportId,
  params: TelemetryDetailParams,
  enabled: boolean,
): UseTelemetryDetailResult {
  const qs = buildTelemetryQuery(report, params);
  const query = useQuery<TelemetryReportResponse, Error>({
    queryKey: ['telemetry-detail', report, params.hours, params.status ?? '', params.route ?? '', params.email ?? ''],
    queryFn: () => apiGet<TelemetryReportResponse>(`${TELEMETRY_API_BASE}/telemetry/detail?${qs.toString()}`),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
  };
}

/**
 * Triggers a browser download of a report's (or drill-down's) Excel export.
 * Calls GET .../telemetry/export?report=...&hours=...(&status&route&email)
 * and saves the blob. Mirrors downloadOmniXlsx / downloadOrdersXlsx.
 */
export async function downloadTelemetryXlsx(
  report: TelemetryReportId | TelemetryDetailReportId,
  hours: number,
  params?: Omit<TelemetryDetailParams, 'hours'>,
): Promise<void> {
  const qs = buildTelemetryQuery(report, { hours, ...params });
  const url = `${TELEMETRY_API_BASE}/telemetry/export?${qs.toString()}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? `telemetry-${report}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Human-readable byte count for the "scanned" footnote. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

/**
 * Insights returns every value as a string. Numeric columns read better right
 * aligned and rounded. Date/time columns (`ts`, `last_seen`) are ISO 8601
 * strings from the api and render through `formatDateTime`.
 */
export function formatCell(column: string, value: string): string {
  if (value === '' || value === undefined) return '—';
  if (column === 'ts' || column === 'last_seen') {
    return formatDateTime(value);
  }
  if (isNumericColumn(column)) {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    return n >= 100 ? Math.round(n).toLocaleString() : String(Math.round(n * 10) / 10);
  }
  return value;
}

export function isNumericColumn(column: string): boolean {
  return (
    column === 'views' ||
    column === 'visitors' ||
    column === 'errors' ||
    column === 'requests' ||
    column === 'distinct_paths' ||
    column === 'responseLatencyMs' ||
    column.endsWith('_ms')
  );
}

/** Column header text. The raw Insights field names are terse. */
export function columnLabel(column: string): string {
  const labels: Record<string, string> = {
    ts: 'Date/time',
    site: 'Site',
    path: 'Path',
    views: 'Views',
    visitors: 'Visitors',
    msg: 'Message',
    errors: 'Errors',
    last_seen: 'Last seen',
    route: 'Route',
    requests: 'Requests',
    p95_ms: 'p95 (ms)',
    avg_ms: 'Avg (ms)',
    max_ms: 'Max (ms)',
    name: 'Name',
    email: 'Email',
    source: 'Source',
    distinct_paths: 'Distinct paths',
    status: 'Status',
    httpMethod: 'Method',
    responseLatencyMs: 'Latency (ms)',
    sourceIp: 'IP',
    userAgent: 'User agent',
    event: 'Event',
  };
  return labels[column] ?? column;
}

/** Plain-English label for each `source` value (person|otto|device|anonymous). */
export const SOURCE_LABELS: Record<string, string> = {
  person: 'Person',
  otto: 'Otto (scheduled job)',
  device: 'Store device',
  test: 'Automated test',
  anonymous: 'Anonymous',
};

// ---------------------------------------------------------------------------
// Client-side sort + search (pure, unit-testable)
// ---------------------------------------------------------------------------

/** Compares two raw string cell values for one column: numeric if the column
 * is numeric, else case-insensitive lexicographic. */
export function compareTelemetryCell(a: string, b: string, column: string): number {
  if (isNumericColumn(column)) {
    const an = Number(a);
    const bn = Number(b);
    const aFinite = Number.isFinite(an) && a !== '';
    const bFinite = Number.isFinite(bn) && b !== '';
    if (aFinite && bFinite) return an - bn;
    if (aFinite) return -1;
    if (bFinite) return 1;
    return 0;
  }
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

/** Sorts a copy of `rows` by `column` (no-op when column is null). */
export function sortTelemetryRows(
  rows: TelemetryRow[],
  column: string | null,
  dir: SortDir,
): TelemetryRow[] {
  if (!column) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    const cmp = compareTelemetryCell(a[column] ?? '', b[column] ?? '', column);
    return dir === 'desc' ? -cmp : cmp;
  });
  return copy;
}

/** True when any of `columns` on `row` case-insensitively contains `query`.
 * An empty/whitespace-only query always matches (no filtering applied). */
export function matchesSearch(row: TelemetryRow, columns: string[], query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return columns.some((c) => (row[c] ?? '').toLowerCase().includes(q));
}
